import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { Button } from 'components/buttons'
import { IconArrow } from 'icons'

import { routes } from 'data/routes'
import { CommunityTextSlice } from 'types/prismic'
import { getLocalisedLink, shortArticleDate } from 'utils'

import { IRelatedArticlesProps } from '../types'

const RelatedArticles = (props: IRelatedArticlesProps) => {
  const { locale: languageCode } = useRouter()
  const { t } = useTranslation(['community'])

  const communityLink = useMemo(
    () => getLocalisedLink('community', languageCode),
    [languageCode]
  )

  return (
    <div className=" w-full flex-shrink-0 gap-x-10 px-5 print:hidden xl:px-0">
      <h2 className="mb-5 text-3xl font-semibold">
        {t('more', { ns: 'community' })}
        <i className="dot" />
      </h2>

      <div className="grid w-full grid-cols-1 gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
        {props.latestCommunityPosts.results.map((communityPost, index) => {
          const postDescription = communityPost.data.slices.find(
            (slice) => slice.slice_type === 'community_text'
          ) as CommunityTextSlice | undefined
          const postDate = new Date(communityPost.first_publication_date)
          const diffInDays = shortArticleDate(postDate, languageCode ?? 'en')

          const categoryLink =
            routes[`/community/category/${communityPost?.data?.category}`]?.[
              languageCode
            ] ?? ''

          return (
            <div key={communityPost.id + index} className="flex flex-col">
              <div className="relative h-60 border-white transition-all">
                {communityPost.data.featuredImage &&
                  communityPost.data.featuredImage.url && (
                    <Image
                      src={communityPost.data.featuredImage.url}
                      alt={communityPost.data.featuredImage.alt ?? '#'}
                      fill
                      style={{ objectFit: 'cover', objectPosition: 'top' }}
                    />
                  )}
              </div>

              <div className="flex flex-1 flex-col py-4">
                <h3 className="text-lg font-semibold">
                  {communityPost.data.title}
                </h3>

                <div className="mb-2 text-sm text-gray-500">
                  <span>{diffInDays}</span>
                  <span className="mx-1">{' | '}</span>
                  <Link href={categoryLink}>
                    <span className="underline hover:text-red-500">
                      {t(`category.${communityPost?.data?.category}`)}
                    </span>
                  </Link>
                </div>

                <div className=" text-gray-600 line-clamp-3">
                  {(postDescription?.primary?.text[0] as any)?.text ?? null}
                </div>

                <div className="mt-5 flex w-full flex-1">
                  <div className="flex flex-col justify-end">
                    <Button
                      href={`/${communityLink}/${communityPost.uid}`}
                      className="animated-svg border-b-2 border-red-550 text-red-550 "
                    >
                      <span className="text-red-550">
                        {t('community:readMore')}
                      </span>
                      <IconArrow className="ml-2 text-red-550" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RelatedArticles
